import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Button, Dialog, cn, FURNITURE_GROUPS, FurnitureIcon } from '@easymoving/ui';

/** Field + sheet for picking a furniture-type icon (manual, optional). */
export function FurnitureTypePicker({
  value,
  onChange,
}: {
  value?: string;
  onChange: (v: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('item.chooseType')}
        className="inline-flex w-full items-center justify-between gap-3 bg-surface text-ink text-[15px] px-3 py-2 border border-rule-strong hover:border-ink rounded-md transition-colors"
      >
        {value ? (
          <span className="inline-flex items-center gap-2">
            <span className="size-8 rounded-md bg-petrol-tint flex items-center justify-center">
              <FurnitureIcon type={value} className="size-6" />
            </span>
            {t(`furniture.type.${value}`)}
          </span>
        ) : (
          <span className="text-graphite-soft">{t('item.chooseType')}</span>
        )}
        <ChevronDown className="size-4 opacity-70" />
      </button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('item.chooseType')}
        footer={
          value ? (
            <Button
              variant="ghost"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              {t('item.clearType')}
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-3" data-testid="furniture-grid">
          {FURNITURE_GROUPS.map((g) => (
            <div key={g.group}>
              <h4 className="smallcaps text-graphite mb-1.5">{t(`furniture.group.${g.group}`)}</h4>
              <div className="grid grid-cols-4 gap-2">
                {g.keys.map((k) => (
                  <button
                    key={k}
                    type="button"
                    aria-label={t(`furniture.type.${k}`)}
                    onClick={() => {
                      onChange(k);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex flex-col items-center gap-1 p-2 rounded-lg border transition-colors',
                      value === k
                        ? 'border-petrol bg-petrol-tint'
                        : 'border-rule hover:bg-surface-tint',
                    )}
                  >
                    <span className="size-11 rounded-md bg-petrol-tint flex items-center justify-center">
                      <FurnitureIcon type={k} className="size-8" />
                    </span>
                    <span className="text-[11px] text-graphite leading-tight text-center">
                      {t(`furniture.type.${k}`)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Dialog>
    </>
  );
}
